import * as React from "react";
import {none, option, Option} from "ts-option";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import Invoice, {InvoiceStatus} from "../../../model/invoice/Invoice";
import CurrencyHelper from "../../../helper/CurrencyHelper";
import {CommonDetailsStatusRow} from "../common/CommonDetailsStatusRow";
import {CommonDetailsInfoRowType, default as CommonDetailsInfoRow, DetailsInfoRowItem} from "../common/CommonDetailsInfoRow";
import {CommonDetailsSummary, DetailsSummaryItem} from "../common/CommonDetailsSummary";
import InvoiceActivityStream from "../activityStream/InvoiceActivityStream";
import Lead from "../../../model/lead/Lead";
import Job from "../../../model/job/Job";
import InvoiceActivityTabs, {InvoiceTab} from "../activityTabs/InvoiceActivityTabs";
import {InvoiceEventType} from "../../../model/invoice/event/InvoiceEvent";
import AccountingNote from "../../../model/invoice/note/AccountingNote";
import ButtonSecondary from "../../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import ButtonTertiary from "../../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import {api} from "../../../api/api";
import Toast from "../../helper/Toast";
import {getErrorMessage, notifyBugsnagAndShowToast} from "../../../utils/Utils";
import Payment from "../../../model/payment/Payment";
import {OnFailure, OnSuccess, OnSuccessData} from "../../../store/action/actionHelpers";
import InvoicePartsAndLabourDetailsModal from "./InvoicePartsAndLabourDetailsModal";
import InvoicePartsAndLabourDetails from "../../../model/invoice/InvoicePartsAndLabourDetails";
import InvoiceEmailPreviewModal from "../invoiceEmail/InvoiceEmailPreviewModal";
import IndividualContact from "../../../model/contact/IndividualContact";
import {PaymentsSummary} from "../common/PaymentsSummary";
import InputWithIcon from "../../../hiberUiKit/src/atoms/Input/InputWithIcon";
import InvoicePaymentReleaseModal from "./InvoicePaymentReleaseModal";
import {connect, Dispatch} from "react-redux";
import {approveInvoice, updateInvoice} from "../../../store/action/invoice/invoice";
import {PaymentConfirmation} from "../common/PaymentConfirmation";
import {FullPaymentConfirmation} from "../common/FullPaymentConfirmation";
import PaymentRecommendation from "../../../model/payment/PaymentRecommendation";
import {Tax} from "../SelectTaxRate";
import Account from "../../../model/account/Account";
import {RootState} from "../../../store/reducer/root/RootState";
import ButtonWithDropdown, {ButtonDropdownItem, ButtonVariant} from "../../../hiberUiKit/src/atoms/Buttons/ButtonWithDropdown";
import InvoicePaymentReleaseData from "../../../model/invoice/InvoicePaymentReleaseData";
import InvoiceItemsTable from "./InvoiceItemsTable";
import {InvoiceStatusRenderer} from "./InvoiceStatusRenderer";
import {createInvoiceNote, updateInvoiceNote} from "../../../store/action/note/notes";
import {getPartnerBrand} from "../../../store/view/partnerBrand";
import {getBrand} from "../../../store/view/brand";
import moment = require("moment");
import classNames = require("classnames");
import deepEqual = require("deep-equal");
import _ = require("underscore");

interface ComponentProps {
	/**
	 * Invoice to be displayed.
	 */
	invoice: Option<Invoice>,
	/**
	 * Lead related to invoice.
	 */
	lead: Option<Lead>,
	/**
	 * Job related to invoice.
	 */
	job: Option<Job>,
	/**
	 * Individual contact
	 */
	contact: Option<IndividualContact>
	/**
	 * Redirect to related Lead.
	 */
	redirectToLead: () => void;
	/**
	 * Redirect to related Job.
	 */
	redirectToJob: () => void;
	/**
	 * Redirect to related Bill.
	 */
	redirectToBill: () => void;
	/**
	 * If false, "View & send partner's bill" menu item is not displayed.
	 */
	viewBillVisible: boolean;
	/**
	 * Invoice labour and parts details.
	 */
	labourAndPartsDetails: Option<InvoicePartsAndLabourDetails>;
	/**
	 * Invoice payment release data.
	 */
	invoicePaymentReleaseData: Option<InvoicePaymentReleaseData>;
	/**
	 * Invoice activity events.
	 */
	events: InvoiceEventType[];
	/**
	 * Invoice notes.
	 */
	notes: AccountingNote[];
	/**
	 * Invoice payments.
	 */
	payments: Payment[];
	/**
	 * Payment recommendations.
	 */
	paymentRecommendations: PaymentRecommendation[];
	/**
	 * Accounts available for invoice item.
	 */
	accounts: Option<Account[]>;
	/**
	 * Note id to set active class and focus on it.
	 */
	activeNoteId: string;
	/**
	 * On update invoice success
	 */
	onUpdateInvoiceSuccess: (invoice: Invoice) => void;
	/**
	 * On send invoice success
	 */
	onSendInvoiceSuccess: () => void;
	/**
	 * On payment status change success callback.
	 */
	onPaymentStatusChangeSuccess: (onSuccess: OnSuccess) => void;
	/**
	 * On full payment confirmation callback.
	 */
	onFullPaymentConfirmation: (onSuccess: OnSuccess) => void;
	/**
	 * Successful payment release callback.
	 */
	onSuccessfulPaymentRelease: () => void;
}

interface ReduxProps {
	/**
	 * Vat value
	 */
	vat: Option<number>,
}

interface DispatchProps {
	/**
	 * Update invoice action.
	 * @param {string} jobId job identifier.
	 * @param {Invoice} invoice data to update.
	 * @param {Option<InvoicePartsAndLabourDetails>} labourAndPartsDetails updated invoice adjust data.
	 * @param {OnSuccessData<Invoice>} onSuccess on invoice update success callback.
	 * @param {OnFailure} onFailure on invoice update failure callback.
	 */
	updateInvoice: (jobId: string,
		invoice: Invoice,
		labourAndPartsDetails: Option<InvoicePartsAndLabourDetails>,
		onSuccess: OnSuccessData<Invoice>,
		onFailure: OnFailure) => void
	/**
	 * Approve invoice action.
	 * @param {string} jobId job identifier.
	 * @param {string} invoiceId invoice identifier.
	 * @param {OnSuccessData<Invoice>} onSuccess on invoice approve success callback.
	 * @param {OnFailure} onFailure on invoice approve failure callback.
	 */
	approveInvoice: (jobId: string, invoiceId: string, onSuccess: OnSuccessData<Invoice>, onFailure: OnFailure) => void
	/**
	 * Add invoice note action.
	 * @param invoiceId
	 * @param content
	 * @param onSuccess
	 */
	addInvoiceNote: (invoiceId: string, content: string, onSuccess: OnSuccessData<AccountingNote>) => void;
	/**
	 * Update invoice note action.
	 * @param noteId
	 * @param invoiceId
	 * @param content
	 * @param onSuccess
	 */
	updateInvoiceNote: (noteId: string, invoiceId: string, content: string, onSuccess: OnSuccessData<AccountingNote>) => void;
}

interface State {
	/**
	 * Note selected for edition.
	 */
	noteToEdit: Option<AccountingNote>;
	/**
	 * Invoice after edition changes.
	 */
	invoice: Option<Invoice>;
	/**
	 * Updated invoice customer display name.
	 */
	customerDisplayName: Option<string>,
	/**
	 * Updated invoice date.
	 */
	invoiceDate: Option<moment.Moment>,
	/**
	 * Updated invoice due date.
	 */
	dueDate: Option<moment.Moment>
	/**
	 * Selected tab id.
	 */
	selectedTabId: InvoiceTab;
	/**
	 * Invoice parts and labour details to be updated.
	 */
	updatedPartsAndLabourDetails: Option<InvoicePartsAndLabourDetails>;
	/**
	 * Adjustment note to be added.
	 */
	adjustmentNote: Option<string>;
	/**
	 * Invoice edit mode indicator.
	 */
	invoiceEditMode: boolean;
	/**
	 * Invoice email preview modal visible
	 */
	invoiceEmailPreviewModalVisible: boolean
	/**
	 * Invoice payment release modal visibility indicator.
	 */
	paymentReleaseModalVisible: boolean;
	/**
	 * Indicates if "Adjust labour & parts amount" modal is visible.
	 */
	adjustLabourAndPartsModalVisible: boolean;
	/**
	 * Indicating if request to server is in progress.
	 */
	isLoading: boolean;
}

type Props = ComponentProps & DispatchProps & ReduxProps

/**
 * Invoice details view.
 */
class InvoiceDetailsView extends React.PureComponent <Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			noteToEdit: none,
			invoice: props.invoice,
			customerDisplayName: props.invoice.map(i => i.customerDisplayName),
			invoiceDate: props.invoice.map(i => i.invoiceDate),
			dueDate: props.invoice.flatMap(i => i.dueDate),
			selectedTabId: InvoiceTab.Note,
			updatedPartsAndLabourDetails: props.labourAndPartsDetails,
			adjustmentNote: none,
			invoiceEditMode: false,
			invoiceEmailPreviewModalVisible: false,
			paymentReleaseModalVisible: false,
			adjustLabourAndPartsModalVisible: false,
			isLoading: false
		};
	}

	componentWillReceiveProps(newProps: ComponentProps) {
		if (!deepEqual(this.props.invoice.orNull, newProps.invoice.orNull)) {
			this.setState({invoice: newProps.invoice});
		}
		if (this.state.updatedPartsAndLabourDetails.isEmpty &&
			!deepEqual(this.state.updatedPartsAndLabourDetails.orNull, newProps.labourAndPartsDetails.orNull)) {
			this.setState({updatedPartsAndLabourDetails: newProps.labourAndPartsDetails});
		}
	}

	render() {
		const customerEmail = this.props.contact.flatMap(c => c.getPrimaryEmail()).getOrElse("");
		const renderPaymentReleaseBar = this.state.invoice.map(inv => inv.status === InvoiceStatus.PaidReleasePending).getOrElse(
			false);
		return (
			<div className={"l-page"}>
				{this.renderStatusRow()}
				{this.state.invoiceEditMode && <div className="c-invoice-details_edit-mode-bar">Edit mode</div>}
				{renderPaymentReleaseBar && this.renderPaymentReleaseBar()}
				{this.renderDetailsInfoRow()}
				{this.renderTable()}
				<div className="c-invoice-details_summary_external-container">
					{this.renderSummary()}
				</div>
				{this.props.paymentRecommendations.map(this.renderRecommendationConfirmation)}
				{this.props.invoice.map(inv => {
					return inv.status === InvoiceStatus.PaidNeedsConfirmation &&
						<FullPaymentConfirmation payment={this.props.payments[0]}
												 confirmFullPayment={_.throttle(() => this.confirmFullPayment(inv.id), 300)}
												 isLoading={this.state.isLoading}
												 renderBodyMessage={this.renderFullPaymentItemBodyMessage}/>
				}).orNull}
				<div className="c-common-details_activity-tabs-container">
					<InvoiceActivityTabs invoice={this.state.invoice}
										 noteToEdit={this.state.noteToEdit}
										 addInvoiceNote={this.props.addInvoiceNote}
										 updateInvoiceNote={this.props.updateInvoiceNote}
										 onUpdateNoteSuccess={this.onUpdateNoteSuccess}
										 selectedTabId={this.state.selectedTabId}
										 onTabsSwitch={this.onTabSwitch}/>
				</div>
				{this.state.invoice.map(i =>
					<InvoiceActivityStream invoiceId={i.id}
										   activeNoteId={this.props.activeNoteId}
										   events={this.props.events}
										   notes={this.props.notes}
										   payments={this.props.payments}
										   onEditNoteClick={this.onEditNoteClick}
										   onDeleteNoteSuccess={this.onDeleteNoteSuccess}/>).orNull}
				{this.renderAdjustLabourAndPartsModal()}
				{this.state.invoiceEmailPreviewModalVisible
				&& <InvoiceEmailPreviewModal customerEmail={customerEmail}
											 invoiceId={this.props.invoice.map(i => i.id).orNull}
											 onCloseClicked={this.hideInvoiceEmailPreviewModal}
											 onSendEmail={this.onSendToCustomerClick}
											 isLoading={this.state.isLoading}/>}
				{this.state.paymentReleaseModalVisible && this.state.invoice.map(inv =>
					<InvoicePaymentReleaseModal invoiceId={inv.id}
												invoicePaymentReleaseData={this.props.invoicePaymentReleaseData}
												onClose={() => this.setState({paymentReleaseModalVisible: false})}
												onSuccessfulPaymentRelease={this.props.onSuccessfulPaymentRelease}
												vatRegistered={inv.vatRegistered}/>).orNull}
			</div>
		);
	}

	renderStatusRow = () => {
		const refNumPathItem1 = {
			label: this.props.lead.map(l => `Lead ID# ${l.referenceNumber}`).orNull,
			onClick: this.props.redirectToLead
		};
		const refNumPathItem2 = {
			label: this.props.job.map(j => `Job ID# ${j.referenceNumber}`).orNull,
			onClick: this.props.redirectToJob
		};
		const refNumPathItem3 = this.state.invoice.map(i => `Invoice# ${i.referenceNumber}`).orNull;
		return <CommonDetailsStatusRow icon={Icon.Invoice}
									   title={"Invoice"}
									   onBackClick={this.props.redirectToJob}
									   customStatusComponent={this.state.invoice.map(
										   inv => <InvoiceStatusRenderer invoice={inv}/>).orNull}
									   refNumPath={[refNumPathItem1, refNumPathItem2, refNumPathItem3]}
									   additionalComponent={this.renderButtons()}/>
	};

	renderPaymentReleaseBar = () =>
		<div className="c-invoice-details_release-payment-bar">
			<div className="c-invoice-details_release-payment-bar_indicator">!</div>
			<div className="c-invoice-details_release-payment-bar_label">
				Full customer payment confirmed. <b>Payment release pending</b>
			</div>
			<div className="c-invoice-details_release-payment-bar_button">
				<ButtonPrimary label={"Release payment"} onClick={() => this.setState({paymentReleaseModalVisible: true})}/>
			</div>
		</div>;

	onEditNoteClick = (note: AccountingNote) => this.setState({noteToEdit: option(note)});

	onTabSwitch = (tab: InvoiceTab) => this.setState({selectedTabId: tab});

	onUpdateNoteSuccess = () => {
		this.setState({noteToEdit: none});
	};

	onDeleteNoteSuccess = () => {
		this.setState({noteToEdit: none});
	};

	renderRecommendationConfirmation = (recommendation: PaymentRecommendation) => {
		return (
			<PaymentConfirmation recommendation={recommendation}
								 isLoading={this.state.isLoading}
								 confirmPayment={_.throttle(() => this.confirmPayment(recommendation.id), 300)}
								 rejectPayment={_.throttle(() => this.rejectPayment(recommendation.id), 300)}/>
		)
	};

	confirmPayment = (recommendationId: string) => this.setState({isLoading: true},
		() => api.instance.invoicePaymentApi.confirmInvoicePaymentRecommendation(recommendationId)
			.then(() => {
				this.props.onPaymentStatusChangeSuccess(() => {
					this.setState({isLoading: false});
					Toast.success("Payment confirmed successfully", "Confirm payment");
				});
			}).catch(error => {
				this.setState({isLoading: false});
				notifyBugsnagAndShowToast(error, "Confirm payment")
			})
	);

	rejectPayment = (recommendationId: string) => this.setState({isLoading: true},
		() => api.instance.invoicePaymentApi.rejectInvoicePaymentRecommendation(recommendationId)
			.then(() => {
				this.props.onPaymentStatusChangeSuccess(() => {
					this.setState({isLoading: false});
					Toast.success("Payment rejected successfully", "Reject payment");
				});
			}).catch(error => {
				this.setState({isLoading: false});
				notifyBugsnagAndShowToast(error, "Reject payment")
			})
	);

	renderFullPaymentItemBodyMessage = () =>
		<div>
			Total amount for this invoice has been paid. <b>Please confirm full invoice payment.</b>
		</div>;

	confirmFullPayment = (invoiceId: string) => this.setState({isLoading: true},
		() => api.instance.invoiceApi.confirmFullInvoicePayment(invoiceId)
			.then(() => {
				this.props.onFullPaymentConfirmation(() => {
					this.setState({isLoading: false});
					Toast.success("Full payment confirmed successfully", "Confirm full payment");
				});
			})
			.catch(err => {
				this.setState({isLoading: false});
				notifyBugsnagAndShowToast(err, "Confirm full payment", getErrorMessage(err))
			})
	);

	renderButtons = () => {
		let dropdownItems: ButtonDropdownItem[] = [];
		if (this.props.viewBillVisible) {
			dropdownItems.unshift(new ButtonDropdownItem(InvoiceAction.VIEW_BILL, `View & send partner's bill`, false, false));
		}
		if (this.state.invoice.map(i => i.status == InvoiceStatus.Draft).getOrElse(false)) {
			dropdownItems.unshift(new ButtonDropdownItem(InvoiceAction.APPROVE, 'Approve for send', false, false));
		}
		else {
			dropdownItems.unshift(new ButtonDropdownItem(InvoiceAction.SEND, 'Send to customer', false, false));
		}
		const isAnyPaymentReleased = this.props.invoicePaymentReleaseData.map(
			data => data.hiberPaymentReleased || data.partnerPaymentReleased).getOrElse(false);
		const canInvoiceBeAdjusted = this.props.invoice.map(i => i.allowPaymentBreakdownWithoutTotalChange()).getOrElse(false) &&
			!isAnyPaymentReleased;
		const canInvoiceBeEdited = this.props.invoice.map(inv => inv.canBeEdited()).getOrElse(false) && !isAnyPaymentReleased;
		if (this.state.invoiceEditMode) {
			return (
				<div className="c-common-details_buttons_container">
					<div className="c-common-details_buttons_common-button">
						<ButtonTertiary label={"Cancel"}
										onClick={() => this.setState({
											invoice: this.props.invoice,
											invoiceEditMode: false,
											customerDisplayName: this.state.invoice.map(i => i.customerDisplayName),
											invoiceDate: this.state.invoice.map(i => i.invoiceDate),
											dueDate: this.state.invoice.flatMap(i => i.dueDate),
											updatedPartsAndLabourDetails: this.props.labourAndPartsDetails,
											adjustmentNote: none
										})}/>
					</div>
					<div className="c-common-details_buttons_common-button">
						<ButtonSecondary label={"Save"}
										 onClick={() => this.onSaveClick()}
										 disabled={this.isSaveDisabled()}/>
					</div>
					<div className="c-common-details_buttons_last-button">
						<ButtonPrimary label={"Approve for send"}
									   disabled={this.state.isLoading}
									   onClick={this.saveAndApproveInvoice}/>
					</div>
				</div>
			);
		}
		return (
			<div className="c-common-details_buttons_container">
				<div className="c-common-details_buttons_common-button">
					<ButtonSecondary label={"Invoice details"} onClick={() => this.setState({paymentReleaseModalVisible: true})}/>
				</div>
				{canInvoiceBeAdjusted &&
				<div className="c-common-details_buttons_common-button">
					<ButtonPrimary label={"Invoice adjustment"}
								   onClick={() => this.setState({adjustLabourAndPartsModalVisible: true})}/>
				</div>}
				<div className="c-common-details_buttons_common-button">
					{canInvoiceBeEdited &&
					<ButtonSecondary label={"Edit invoice"}
									 onClick={() => this.setState({invoiceEditMode: true})}/>}
				</div>
				<div className="c-common-details_buttons_last-button">
					<ButtonWithDropdown label={"Invoice actions"}
										isLoading={this.state.isLoading}
										dropdownItems={dropdownItems}
										onDropdownItemClick={this.onActionMenuItemClick}
										buttonVariant={ButtonVariant.PRIMARY}
										displayArrowDown={false}/>
				</div>
			</div>
		);
	};

	onActionMenuItemClick = (actionId: string) => {
		switch (actionId) {
			case InvoiceAction.APPROVE:
				this.approveInvoice();
				return;
			case InvoiceAction.SEND:
				this.openInvoiceEmailPreviewModal();
				return;
			case InvoiceAction.VIEW_BILL:
				this.props.redirectToBill();
				return;
		}
	};

	isSaveDisabled = () => {
		return (
			!this.state.customerDisplayName.getOrElse("").length
			|| this.state.invoiceDate.isEmpty
			|| this.state.updatedPartsAndLabourDetails.map(details => !details.description.length).getOrElse(true)
			|| this.state.isLoading
		);
	};

	openInvoiceEmailPreviewModal = () => this.setState({invoiceEmailPreviewModalVisible: true});
	hideInvoiceEmailPreviewModal = () => this.setState({invoiceEmailPreviewModalVisible: false});

	saveAndApproveInvoice = () => this.onSaveClick(this.approveInvoice);

	approveInvoice = () => {
		this.setState({isLoading: true}, () =>
			this.state.invoice.map((i: Invoice) => {
				this.props.job.map((j: Job) => {
					this.props.approveInvoice(j.id, i.id, (invoice) => {
						this.setState({
								invoice: option(invoice),
								dueDate: invoice.dueDate,
								invoiceDate: option(invoice.invoiceDate),
								customerDisplayName: option(invoice.customerDisplayName),
								isLoading: false
							},
							() => {
								Toast.success("Invoice approved successfully", "Invoice approve");
								this.state.adjustmentNote.map(noteContent => {
									this.addNote(i.id, noteContent, () => {
											this.setState({adjustmentNote: none});
											this.props.onUpdateInvoiceSuccess(invoice);
										}
									);
								}).getOrElse(() => this.props.onUpdateInvoiceSuccess(invoice));
							});
					}, (error) => {
						notifyBugsnagAndShowToast(error, "Invoice approve");
						this.setState({isLoading: false});
					})
				})
			}));
	};

	onSendToCustomerClick = (customerEmail: string, subject: string) => {
		this.setState({isLoading: true}, () => this.state.invoice.map((i: Invoice) => {
			this.props.job.map((j: Job) => {
				api.instance.invoiceApi.sendInvoiceEmail(i.id, customerEmail, subject)
					.then(() => {
						this.setState({invoiceEmailPreviewModalVisible: false, isLoading: false});
						Toast.success("Invoice sent successfully", "Send invoice");
						this.props.onSendInvoiceSuccess();
					})
					.catch((error) => {
						notifyBugsnagAndShowToast(error, "Send invoice", getErrorMessage(error));
						this.setState({isLoading: false});
					});
			})
		}));
	};

	onSaveClick = (onSuccess?: OnSuccess) => {
		this.setState({isLoading: true}, () => {
			const invoice = this.state.invoice.map(i => i.copy({
				customerDisplayName: this.state.customerDisplayName.orNull,
				invoiceDate: this.state.invoiceDate.orNull,
				dueDate: this.state.dueDate
			}));

			invoice.map((i: Invoice) => {
				if (i.items.every(item => item.canBeSubmitted())) {
					// if objects are equal, 'none' is passed to avoid unnecessary api call
					const partsAndLabourDetailsToUpdate =
						deepEqual(this.props.labourAndPartsDetails.orNull, this.state.updatedPartsAndLabourDetails.orNull) ?
							none : this.state.updatedPartsAndLabourDetails;
					this.props.job.map((j: Job) => {
						this.props.updateInvoice(j.id, i, partsAndLabourDetailsToUpdate,
							(updatedInvoice) => {
								this.setState({invoice: option(updatedInvoice), invoiceEditMode: false, isLoading: false},
									onSuccess ? onSuccess :
										() => {
											Toast.success("Invoice updated successfully", "Update invoice");
											this.state.adjustmentNote.map(noteContent => {
												this.addNote(i.id, noteContent, () => {
													this.setState({adjustmentNote: none});
													this.props.onUpdateInvoiceSuccess(updatedInvoice)
												});
											}).getOrElse(() => this.props.onUpdateInvoiceSuccess(updatedInvoice));
										});
							}, (error) => {notifyBugsnagAndShowToast(error, "Update invoice")});
					});
				}
				else {
					Toast.error("Not all required item fields are filled", "Update invoice");
				}
			});
		});
	};

	addNote = (invoiceId: string, noteContent: string, onSuccess: OnSuccess) => {
		const successCallback = () => {
			Toast.success("Note created successfully", "Create note");
			onSuccess();
		};
		this.props.addInvoiceNote(invoiceId, noteContent, successCallback);
	};

	updateInvoiceAfterPartsAndLabourEdit = (onSuccess?: OnSuccess) =>
		this.state.updatedPartsAndLabourDetails.map(details => {
			this.state.invoice.map((inv: Invoice) => {
				const itemToUpdate = inv.getLabourAndPartsItem();
				const newLabourAndPartsAmount = details.getTotal(inv.vatRegistered, this.props.vat);
				const updatedItem = itemToUpdate.withNewAmount(newLabourAndPartsAmount);
				const updatedInvoice = inv.withUpdatedLabourAndPartsItem(updatedItem);
				this.setState({invoice: option(updatedInvoice)}, () => {
					onSuccess && onSuccess();
				});
			})
		}).orNull;

	renderDetailsInfoRow = () => {
		const detailsInfoRowItem1: DetailsInfoRowItem = {
			label: "To",
			value: this.state.customerDisplayName.orNull,
			itemType: CommonDetailsInfoRowType.String,
			onChange: (customerDisplayName) => this.setState({customerDisplayName: option(customerDisplayName)})
		};
		const detailsInfoRowItem2: DetailsInfoRowItem = {
			label: "Invoice date",
			value: this.state.invoiceDate.orNull,
			itemType: CommonDetailsInfoRowType.Date,
			onChange: (invoiceDate) => this.setState({invoiceDate}),
			customInput: this.renderCustomInput(this.state.invoiceDate)
		};
		const detailsInfoRowItem3: DetailsInfoRowItem = {
			label: "Due date",
			value: this.state.dueDate.orNull,
			itemType: CommonDetailsInfoRowType.Date,
			onChange: (dueDate) => this.setState({dueDate}),
			clearable: true,
			customInput: this.renderCustomInput(this.state.dueDate)
		};
		const detailsInfoRowItem4: DetailsInfoRowItem = {
			label: "Invoice #",
			value: this.state.invoice.flatMap(inv => inv.invoiceNo).orNull
		};
		const detailsInfoRowItem5: DetailsInfoRowItem = {
			label: "Reference",
			value: this.state.invoice.map(inv => inv.referenceNumber).orNull
		};
		const detailsInfoRowItems = [detailsInfoRowItem1, detailsInfoRowItem2, detailsInfoRowItem3, detailsInfoRowItem4,
			detailsInfoRowItem5];
		return <CommonDetailsInfoRow items={detailsInfoRowItems} editMode={this.state.invoiceEditMode}/>;
	};

	renderCustomInput = (date: Option<moment.Moment>) => {
		return (
			<InputWithIcon type={"text"}
						   value={date.map(d => d.date().toString()).getOrElse("")}
						   appendIcon={Icon.Calendar}
						   iconAppendCustomClass={"c-invoice-details_info-row_input-icon"}/>
		)
	};

	renderTable = () => {
		return (
			<InvoiceItemsTable invoice={this.state.invoice}
							   editMode={this.state.invoiceEditMode}
							   accounts={this.props.accounts}
							   vat={this.props.vat}
							   labourAndPartsItemDescription={this.state.updatedPartsAndLabourDetails.map(
								   details => details.description)}
							   onLabourAndPartsItemDescriptionChange={this.onPartsAndLabourDescriptionChange}
							   onLabourAndPartsItemEditClick={this.onLabourAndPartsModalOpen}
							   onChange={this.onInvoiceChange}/>
		);
	};

	onPartsAndLabourDescriptionChange = (description: string) => this.setState({
		updatedPartsAndLabourDetails: this.state.updatedPartsAndLabourDetails.map(details => details.copy({description}))
	});

	onInvoiceChange = (invoice: Invoice) => this.setState({invoice: option(invoice)});

	/**
	 * Render adjust labour and parts modal.
	 */
	renderAdjustLabourAndPartsModal = () => {
		if (this.state.adjustLabourAndPartsModalVisible) {
			return (
				this.state.invoice.map(inv => {
					return (
						<InvoicePartsAndLabourDetailsModal onSubmit={this.onLabourAndPartsModalSubmit}
														   onCancel={this.onLabourAndPartsModalCancel}
														   labourAndPartsDetails={this.state.updatedPartsAndLabourDetails}
														   note={this.state.adjustmentNote}
														   invoiceId={inv.id}
														   vatRegistered={inv.vatRegistered}
														   vat={this.props.vat}
														   allowTotalAmountChange={this.state.invoiceEditMode}/>
					);
				}).orNull
			);
		}
	};

	onLabourAndPartsModalSubmit = (data: InvoicePartsAndLabourDetails, noteContent: string) => {
		let onSuccess: () => any;
		if (!this.state.invoiceEditMode) {
			onSuccess = () => this.updateInvoiceAfterPartsAndLabourEdit(this.onSaveClick);
		}
		else {
			onSuccess = this.updateInvoiceAfterPartsAndLabourEdit;
		}
		this.setState({
			updatedPartsAndLabourDetails: option(data),
			adjustmentNote: option(noteContent),
			adjustLabourAndPartsModalVisible: false
		}, onSuccess);
	};

	onLabourAndPartsModalOpen = () => this.setState({adjustLabourAndPartsModalVisible: true});
	onLabourAndPartsModalCancel = () => this.setState({adjustLabourAndPartsModalVisible: false});

	renderSummary = () => {
		const isAnyPaymentOrRecommendation = this.props.paymentRecommendations.length || this.props.payments.length;
		const summaryItem1: DetailsSummaryItem = {
			label: "Subtotal",
			value: this.state.invoice.map(inv => CurrencyHelper.toCurrency(inv.subtotal)).orNull
		};
		const summaryItem2: DetailsSummaryItem = {
			label: "Tax",
			value: this.state.invoice.flatMap(inv => inv.tax.map(t => CurrencyHelper.toCurrency(t))).getOrElse("-")
		};
		const lastSummaryItem: DetailsSummaryItem = {
			label: "Total",
			value: this.state.invoice.map(inv => CurrencyHelper.toCurrency(inv.total)).orNull
		};
		const classFromProps = classNames({
			"c-invoice-details_summary_container": true,
			"edit-mode": this.state.invoiceEditMode
		});
		return (
			<CommonDetailsSummary items={[summaryItem1, summaryItem2]}
								  totalItem={lastSummaryItem}
								  customClass={classFromProps}
								  extraWidth={true}>
				{isAnyPaymentOrRecommendation ? this.renderPayments() : null}
			</CommonDetailsSummary>
		);
	};

	renderPayments = () => this.state.invoice.map(inv =>
		<PaymentsSummary totalValue={inv.total}
						 payments={this.props.payments}
						 paymentRecommendations={this.props.paymentRecommendations}/>
	).orNull;
}

function mapStateToProps(state: RootState, props: ComponentProps) {
	const partnerBrand = props.invoice.map(i => i.partnerBrandId).flatMap(id => getPartnerBrand(id, state));
	const brand = partnerBrand.map(pb => pb.brandId).flatMap(id => getBrand(id, state));
	const vat = brand.map(b => b.vatRate);
	return {
		vat
	}
}

// TODO: move this to InvoiceDetails
function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		updateInvoice: (jobId: string,
			invoice: Invoice,
			labourAndPartsDetails: Option<InvoicePartsAndLabourDetails>,
			onSuccess: OnSuccessData<Invoice>,
			onFailure: OnFailure) => dispatch(
			updateInvoice(jobId, invoice, labourAndPartsDetails, onSuccess, onFailure)),
		approveInvoice: (jobId: string, invoiceId: string, onSuccess: OnSuccessData<Invoice>, onFailure: OnFailure) => dispatch(
			approveInvoice(jobId, invoiceId, onSuccess, onFailure)
		),
		addInvoiceNote: (invoiceId: string, content: string, onSuccess: OnSuccess) => dispatch(
			createInvoiceNote(invoiceId, content, onSuccess, (error) => notifyBugsnagAndShowToast(error, "Create Note error"))
		),
		updateInvoiceNote: (noteId: string, invoiceId: string, content: string, onSuccess: OnSuccess) => dispatch(
			updateInvoiceNote(noteId, invoiceId, content, onSuccess,
				(error) => notifyBugsnagAndShowToast(error, "Update Note error"))
		)
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceDetailsView)

enum InvoiceAction {
	APPROVE = "APPROVE",
	SEND = 'SEND',
	VIEW_BILL = 'VIEW_BILL'
}
